import { ScaleElement } from '@scale/base-element';
import { fetchTextWithTimeout } from '@scale/utilities';

class ScaleSearchOverlay extends ScaleElement {
  #cache = new Map();
  #fetchController;

  setup() {
    const input = this.ref('search-input');
    if (!input) return;

    const predictive = !this.hasAttribute('data-no-predictive');

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#close();
        return;
      }

      if (!predictive) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.#navigateResults(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.#navigateResults(-1);
          break;
        case 'Enter': {
          const focused = this.ref('results')?.querySelector('.is-focused a');
          if (focused) {
            e.preventDefault();
            focused.click();
          }
          break;
        }
      }
    }, { signal: this.signal });

    if (!predictive) return;

    input.addEventListener('input', () => {
      this.debounce('search', () => this.#onInput(), 300);
    }, { signal: this.signal });
  }

  openSearch() {
    this.hidden = false;
    this.ref('search-input')?.focus();
  }

  closeSearch() {
    this.#close();
  }

  clearSearch() {
    const input = this.ref('search-input');
    const results = this.ref('results');
    const clearBtn = this.ref('clear-btn');

    if (input) {
      input.value = '';
      input.focus();
    }
    this.#cancelRequest();
    if (results) {
      results.innerHTML = '';
      results.removeAttribute('aria-busy');
    }
    if (clearBtn) clearBtn.hidden = true;

    input?.setAttribute('aria-expanded', 'false');
  }

  #close() {
    this.#cancelRequest();
    this.hidden = true;
    const input = this.ref('search-input');
    if (input) {
      input.value = '';
      input.setAttribute('aria-expanded', 'false');
    }
    const results = this.ref('results');
    if (results) {
      results.innerHTML = '';
      results.removeAttribute('aria-busy');
    }
  }

  async #onInput() {
    const input = this.ref('search-input');
    const query = input?.value.trim();
    const clearBtn = this.ref('clear-btn');

    if (clearBtn) clearBtn.hidden = !query;

    if (!query || query.length < 2) {
      this.#cancelRequest();
      const results = this.ref('results');
      if (results) {
        results.innerHTML = '';
        results.removeAttribute('aria-busy');
      }
      input?.setAttribute('aria-expanded', 'false');
      return;
    }

    const cached = this.#cache.get(query);
    if (this.#cache.has(query)) {
      this.#cancelRequest();
      this.#renderResults(cached);
      return;
    }

    const locale = window.Shopify?.routes?.root || '/';
    const url = `${locale}search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product,collection,page,article,query&resources[limit]=4`;

    this.#fetchController?.abort();
    const controller = new AbortController();
    this.#fetchController = controller;
    this.ref('results')?.setAttribute('aria-busy', 'true');

    try {
      const { response, text: html } = await fetchTextWithTimeout(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('#predictive-search-results');
      if (!incoming) throw new Error('Malformed predictive search response');
      const content = incoming.innerHTML;

      this.#cache.set(query, content);
      if (this.#cache.size > 20) {
        const oldest = this.#cache.keys().next().value;
        this.#cache.delete(oldest);
      }

      if (this.#fetchController === controller && input?.value.trim() === query) {
        this.#renderResults(content);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && this.#fetchController === controller && input?.value.trim() === query) {
        this.#renderResults('');
      }
    } finally {
      if (this.#fetchController === controller) {
        this.#fetchController = null;
        this.ref('results')?.removeAttribute('aria-busy');
      }
    }
  }

  #navigateResults(direction) {
    const results = this.ref('results');
    if (!results) return;

    const items = [...results.querySelectorAll('[role="option"]')];
    if (!items.length) return;

    const current = items.findIndex((el) => el.classList.contains('is-focused'));
    const next = Math.max(0, Math.min(items.length - 1, current + direction));

    items.forEach((el) => el.classList.remove('is-focused'));
    items[next].classList.add('is-focused');
    items[next].scrollIntoView({ block: 'nearest' });

    this.ref('search-input')?.setAttribute('aria-activedescendant', items[next].id);
  }

  #renderResults(html) {
    const results = this.ref('results');
    const input = this.ref('search-input');
    if (results) results.innerHTML = html;
    input?.setAttribute('aria-expanded', html ? 'true' : 'false');
    input?.removeAttribute('aria-activedescendant');
    results?.removeAttribute('aria-busy');
  }

  #cancelRequest() {
    this.#fetchController?.abort();
    this.#fetchController = null;
  }
}

if (!customElements.get('scale-search-overlay')) {
  customElements.define('scale-search-overlay', ScaleSearchOverlay);
}
